import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { IconButton, Typography, Box } from '@mui/material';
import { Mic, MicOff } from '@mui/icons-material';
import { useSpeechTokenService } from '../services/speechTokenService';
import socketService, { EnhancedChatMessage } from '../services/socketService';
import { stopGlobalAudio } from '../utils/streamAudioPlayer';
import { useAIResponse } from '../hooks/useAIResponse';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { useAzureSpeechSynthesis } from '../hooks/useAzureSpeechSynthesis';
import { useMicrophone } from '../hooks/useMicrophone';
import { useSpeechTokenRefresh } from '../hooks/useSpeechTokenRefresh';
import { useTranscribedEvent } from '../hooks/useTranscribedEvents';
import { logMessage } from '../utils/logMessage';

interface VoiceOnlyInputProps {
  userName: string;
  contactName: string;
  assessment: any;
  onTranscript: (text: string, speaker: string) => void;
  onSpeaking: (who: string | null) => void;
  conversationId: string | null;
  isMuted: boolean;
  volume: number;
  userMuted: boolean;
  blockMic: boolean;
  setUserMuted: (b: boolean) => void;
  setBlockMic: (b: boolean) => void;
  onFirstAIVoice: () => void;
  waitingForFirstAI: boolean;
  socketError: string | null;
  setSocketError: (msg: string | null) => void;
  setBadState: (b: boolean) => void;
}

// Helper function for robust credential check
function areSpeechCredentialsReady(creds: { token: string; region: string } | null): boolean {
  return (
    !!creds &&
    typeof creds.token === 'string' &&
    creds.token.length > 0 &&
    typeof creds.region === 'string' &&
    creds.region.length > 0
  );
}

const VoiceOnlyInput = forwardRef<any, VoiceOnlyInputProps>(({
  userName,
  contactName,
  assessment,
  onTranscript,
  onSpeaking,
  conversationId,
  isMuted,
  volume,
  userMuted,
  blockMic,
  setUserMuted,
  setBlockMic,
  onFirstAIVoice,
  waitingForFirstAI,
  socketError,
  setSocketError,
  setBadState
}, ref) => {
  // Fetch speech token for clientside TTS
  const { fetchSpeechToken } = useSpeechTokenService();

  // Clientside TTS through azure speech SDK
  const speakTextWithAzureSDK = useAzureSpeechSynthesis({ isMuted, volume, setBlockMic, setStatusText: setSocketError });

  // Microphone logic and socket events for streaming audio and handling of stop recognition event from server
  const { micEnabled, toggleMicEnabled } = useMicrophone(userName, onSpeaking, userMuted, blockMic);

  // Audio playback and state changes related to audio playback, who is speaking on the call page and reenabling user mic
  const { playAudio } = useAudioPlayback(contactName, onSpeaking, setBlockMic, toggleMicEnabled, isMuted, volume);

  // Reference for last EnhancedChatMessage for sos recovery
  const lastMessageRef = useRef<EnhancedChatMessage | null>(null);

  // Silent Token refresh for speech sdk
  const { speechCredentials, setSpeechCredentials } = useSpeechTokenRefresh(fetchSpeechToken);

  // Set up transcribed event hook, changes onTranscript to trigger useAIResponse
  useTranscribedEvent(conversationId, userName, assessment, onTranscript, onSpeaking, toggleMicEnabled, setBlockMic);

  // Keep mic disabled when userMuted is true
  useEffect(() => {
    toggleMicEnabled(!userMuted);
  }, [userMuted, toggleMicEnabled]);

  // Unblock user mic on error and enable sos recovery
  useEffect(() => {
    function handleSocketError(error: { message: string }) {
      setSocketError('Something went wrong, please unmute to try again');
      logMessage(
        '[VoiceOnlyInput] Socket error received', 
        'error',
        {error: error}
      );
      setBlockMic(false);
      setUserMuted(true)
      toggleMicEnabled(false)
      setBadState(true);
    }
    socketService.onError(handleSocketError);
    return () => {
      // Clean up the error listener
      socketService.socket?.off('error', handleSocketError);
    };
  }, [setBlockMic, setSocketError, setUserMuted, toggleMicEnabled]);

  // Keep a ref to map conversationId to recognition timestamp
  const recognitionTimestampsRef = useRef<Map<string, number>>(new Map());

  // Keep a ref to map conversationId to AI generation start timestamp
  const aiRequestTimestampsRef = useRef<Map<string, number>>(new Map());

  // Buffer AI responses that arrive before speech credentials are ready
  const pendingAIResponseRef = useRef<EnhancedChatMessage | null>(null);

  // Audio cleanup on unmount
  useEffect(() => {
    return () => {
      stopGlobalAudio();
    };
  }, []);

  // Hook for ai response, will play audio for ai response if using a tts model. 
  useAIResponse({
    speechCredentials,
    assessment,
    contactName,
    onTranscript,
    onFirstAIVoice,
    setBlockMic,
    speakTextWithAzureSDK,
    playAudio,
    setSocketError,
    setUserMuted,
    setBadState,
    fetchSpeechToken,
    setSpeechCredentials,
    recognitionTimestampsRef,
    aiRequestTimestampsRef,
    pendingAIResponseRef,
    lastMessageRef,
  });

  const handleSosRecovery = async () => {
    setSocketError(null);
    setBadState(false);

    try {
      const { token, region } = await fetchSpeechToken();
      setSpeechCredentials({ token, region });

      if (lastMessageRef.current && lastMessageRef.current.message) {
        await speakTextWithAzureSDK({
          text: lastMessageRef.current.message,
          token,
          region,
          voiceName: assessment.voiceName || "en-US-AvaMultilingualNeural",
          onEnd: () => {
            setBlockMic(false);
            setSocketError(null);
            setBadState(false);
          },
          onError: (e) => {
            logMessage("[AZURE_SPEACH] Error when attempting AI speech", "error", e)
            setBlockMic(false);
            setSocketError("Please use the sos button to attempt to recover the session.");
            setBadState(true);
          },
          recognitionTimestamp: lastMessageRef.current.conversation_id
            ? recognitionTimestampsRef.current.get(lastMessageRef.current.conversation_id)
            : undefined,
          setBadState,
          fetchSpeechToken,
          setSpeechCredentials,
        });
      } else {
        setSocketError("No last message to retry.");
      }
    } catch (err: any) {
      logMessage("[Uncaught in SOS]", "error", err)
      setSocketError("Failed to refresh speech token or recover TTS.");
      setBadState(true);
    }
  };

  // UI status
  let statusText: string;
  if (userMuted) {
    statusText = "Mic muted";
  } else if (blockMic) {
    statusText = contactName + " is speaking...";
  } else if (micEnabled) {
    statusText = "Listening...";
  } else {
    statusText = "Press mic to speak";
  }

  // Raising handleSosRecovery to meeting grid
  useImperativeHandle(ref, () => ({
    handleSosRecovery
  }));

  // Don't render controls until ready
  if (waitingForFirstAI || !areSpeechCredentialsReady(speechCredentials)) return null;

  let iconButtonColor:
    | "inherit"
    | "error"
    | "primary"
    | "default"
    | "secondary"
    | "info"
    | "success"
    | "warning";

  if (userMuted) {
    iconButtonColor = "error";
  } else if (!micEnabled && !blockMic) {
    iconButtonColor = "error";
  } else if (micEnabled) {
    iconButtonColor = "primary";
  } else {
    iconButtonColor = "inherit";
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 2, justifyContent: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          color={iconButtonColor}
          aria-label={micEnabled ? "Stop Listening" : "Start Listening"}
          disabled={blockMic || userMuted || !conversationId}
        >
          {micEnabled && !userMuted ? <Mic /> : <MicOff />}
        </IconButton>
        <Typography variant="body2" color={userMuted || micEnabled ? "primary" : "text.secondary"}>
          {statusText}
        </Typography>
      </Box>
      {socketError && (
        <Typography variant="body1" color="error" sx={{ mt: 1, fontSize:"large" }}>
          {socketError}
        </Typography>
      )}
    </Box>
  );
});

export default VoiceOnlyInput;